import React from "react";
import { FileText, Scale, Building2, Check } from "lucide-react";

// ── Firm Type Definitions ─────────────────────────────────────────────

interface FirmTypeOption {
  value: "solicitor" | "litigation" | "full_service";
  label: string;
  description: string;
  examples: string[];
  icon: React.FC<any>;
  color: string;
  iconColor: string;
  borderColor: string;
}

const FIRM_TYPE_OPTIONS: FirmTypeOption[] = [
  {
    value: "solicitor",
    label: "Solicitor (Transactional)",
    description:
      "Your firm primarily handles non-contentious legal work  -  deals, documentation, and advisory services.",
    examples: [
      "Real estate closings",
      "Corporate transactions",
      "Wills & estate planning",
      "Contract drafting",
    ],
    icon: FileText,
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    borderColor: "border-blue-500",
  },
  {
    value: "litigation",
    label: "Litigation (Barrister)",
    description:
      "Your firm primarily represents clients in disputes  -  court appearances, hearings, and trials.",
    examples: [
      "Civil disputes",
      "Criminal defence",
      "Appeals",
      "Administrative tribunals",
    ],
    icon: Scale,
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    borderColor: "border-purple-500",
  },
  {
    value: "full_service",
    label: "Full Service (Both)",
    description:
      "Your firm handles both transactional and litigation matters across practice areas.",
    examples: [
      "Transactional & disputes",
      "Advisory & advocacy",
      "End-to-end legal services",
    ],
    icon: Building2,
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    borderColor: "border-emerald-500",
  },
];

// ── Component ──────────────────────────────────────────────────────────

interface FirmTypeStepProps {
  selected: "solicitor" | "litigation" | "full_service";
  onChange: (type: "solicitor" | "litigation" | "full_service") => void;
}

const FirmTypeStep: React.FC<FirmTypeStepProps> = ({ selected, onChange }) => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">
          What type of firm are you?
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          This helps us tailor the interface and workflows to how your firm
          operates.
        </p>
      </div>

      {/* Firm type cards */}
      <div className="space-y-3">
        {FIRM_TYPE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = selected === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`relative w-full text-left p-5 rounded-xl border-2 transition-all ${
                isSelected
                  ? `${option.borderColor} ${option.color} shadow-sm`
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
              }`}
            >
              {/* Check indicator */}
              {isSelected && (
                <div
                  className={`absolute top-4 right-4 h-6 w-6 rounded-full flex items-center justify-center ${
                    option.value === "solicitor"
                      ? "bg-blue-500"
                      : option.value === "litigation"
                      ? "bg-purple-500"
                      : "bg-emerald-500"
                  }`}
                >
                  <Check className="h-3.5 w-3.5 text-white" />
                </div>
              )}

              <div className="flex items-start gap-4">
                <div
                  className={`h-11 w-11 rounded-xl ${option.color} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`h-5 w-5 ${option.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0 pr-8">
                  <p className="text-base font-semibold text-slate-800">
                    {option.label}
                  </p>
                  <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                    {option.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {option.examples.map((ex) => (
                      <span
                        key={ex}
                        className="inline-flex items-center px-2.5 py-1 rounded-lg bg-white/80 border border-slate-200 text-xs text-slate-600"
                      >
                        {ex}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <p className="text-xs text-slate-400">
        You can change this anytime in Settings &rarr; Firm Profile
      </p>
    </div>
  );
};

export default FirmTypeStep;
